import get from 'lodash.get';
import type { FeedEntry, UIComponent } from '~/services/layout';
import { ActionRegistry } from '../actions/ActionRegistry';
import type { Action } from '../actions/types';

/**
 * Splits a screen-state multi-select value using the native provider format.
 *
 * @param value - Comma-delimited selection string.
 * @returns Selected tags, with empty segments dropped.
 */
export function parseMultiSelectValue(value: string | undefined): string[] {
  if (!value) return [];

  return value.split(',').filter((segment) => segment.length > 0);
}

/**
 * Joins selected tags using the native provider format.
 *
 * @param values - Selected tags.
 * @returns Comma-delimited selection string.
 */
export function serializeMultiSelectValue(values: string[]): string {
  return values.join(',');
}

/**
 * Adds or removes a tag from a comma-delimited multi-select value.
 *
 * @param selected - Current comma-delimited selection.
 * @param tag - Tag to toggle.
 * @returns Updated comma-delimited selection.
 */
export function toggleMultiSelectValue(selected: string, tag: string): string {
  const selectedValues: string[] = parseMultiSelectValue(selected);

  if (selectedValues.includes(tag)) {
    return serializeMultiSelectValue(
      selectedValues.filter((value) => value !== tag)
    );
  }

  return serializeMultiSelectValue([...selectedValues, tag]);
}

/**
 * Returns whether a preference-editor tag is in the stored selection.
 *
 * @param selected - Stored selection string.
 * @param feedEntryExtensionsTag - Cell tag to test.
 * @returns True when the tag is selected.
 */
function isPreferenceEditorTagSelected({
  selected,
  feedEntryExtensionsTag,
  isMultiSelect,
}: {
  selected: string;
  feedEntryExtensionsTag: string | undefined;
  isMultiSelect: boolean;
}): boolean {
  if (!feedEntryExtensionsTag) return false;

  if (isMultiSelect) {
    return parseMultiSelectValue(selected).includes(feedEntryExtensionsTag);
  }

  return selected === feedEntryExtensionsTag;
}

type CellSelectionArgs = {
  isPreferenceEditor: boolean;
  selected: string;
  feedEntryExtensionsTag: string | undefined;
  feedEntryId?: string;
  behaviorCurrentSelection: string | undefined;
  isBehaviorMultiActionFeedEntry: boolean;
  isMultiSelect: boolean;
  activeProfileId?: string | null;
};

/**
 * Whether a tap-action cell represents the currently active profile.
 */
function isActiveProfileBehaviorCellSelected({
  feedEntryExtensionsTag,
  feedEntryId,
  behaviorCurrentSelection,
  activeProfileId,
}: Pick<
  CellSelectionArgs,
  | 'feedEntryExtensionsTag'
  | 'feedEntryId'
  | 'behaviorCurrentSelection'
  | 'activeProfileId'
>): boolean {
  if (behaviorCurrentSelection !== undefined) {
    return behaviorCurrentSelection === feedEntryExtensionsTag;
  }

  if (!activeProfileId) {
    return false;
  }

  return (
    feedEntryExtensionsTag === activeProfileId ||
    feedEntryId === activeProfileId
  );
}

export const getSelectedCell = ({
  isPreferenceEditor,
  selected,
  feedEntryExtensionsTag,
  feedEntryId,
  behaviorCurrentSelection,
  isBehaviorMultiActionFeedEntry,
  isMultiSelect,
  activeProfileId,
}: CellSelectionArgs) => {
  if (isBehaviorMultiActionFeedEntry) {
    return isActiveProfileBehaviorCellSelected({
      feedEntryExtensionsTag,
      feedEntryId,
      behaviorCurrentSelection,
      activeProfileId,
    });
  }

  return (
    isPreferenceEditor &&
    isPreferenceEditorTagSelected({
      selected,
      feedEntryExtensionsTag,
      isMultiSelect,
    })
  );
};

export const getUnselectedCell = ({
  isPreferenceEditor,
  selected,
  feedEntryExtensionsTag,
  feedEntryId,
  behaviorCurrentSelection,
  isBehaviorMultiActionFeedEntry,
  isMultiSelect,
  activeProfileId,
}: CellSelectionArgs) => {
  if (isBehaviorMultiActionFeedEntry) {
    return !isActiveProfileBehaviorCellSelected({
      feedEntryExtensionsTag,
      feedEntryId,
      behaviorCurrentSelection,
      activeProfileId,
    });
  }

  return (
    isPreferenceEditor &&
    !isPreferenceEditorTagSelected({
      selected,
      feedEntryExtensionsTag,
      isMultiSelect,
    })
  );
};

export const selectParentEntryExtensions = (entry: FeedEntry) =>
  get(entry, '_extensions');

export const selectEntryParentBehavior = (
  entry: FeedEntry
): {
  selectMode: 'single' | 'multiple' | undefined;
  currentSelection: string | undefined;
} => {
  return {
    selectMode: get(selectParentEntryExtensions(entry), 'behavior.select_mode'),
    currentSelection: get(
      selectParentEntryExtensions(entry),
      'behavior.current_selection'
    ),
  };
};

export const selectEntryParentPreferenceEditorOptions = (entry: FeedEntry) => {
  return get(selectParentEntryExtensions(entry), 'preference_editor_options');
};

export const executeCellActions = (
  tapActionsExtension: { actions?: Action[] } | null | undefined,
  context: { feedEntry: FeedEntry; uiComponent: UIComponent }
) => {
  const actions = tapActionsExtension?.actions;
  if (!Array.isArray(actions)) return;

  ActionRegistry.getInstance().execute(actions, { context });
};
